import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plug } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { SOP_AUTH_TYPES, sopConfig, sopTestResult } from '../../lib/types'

/**
 * Company Admin SOP integration — mirrors the Super Admin
 * `SopIntegrationPage` exactly. Same fields, same layout, same actions;
 * the only difference is the API surface — this page reads/writes
 * `/integrations/sop` (tenant-scoped) instead of
 * `/super-admin/integrations/sop` (platform-wide).
 */
const AUTH_LABEL = {
  none: 'No authentication',
  api_key: 'API key (sent as X-API-Key header)',
  bearer: 'Bearer token (sent as Authorization: Bearer <token>)',
}

export function IntegrationsPage() {
  const qc = useQueryClient()
  const [endpointUrl, setEndpointUrl] = useState('')
  const [authType, setAuthType] = useState('none')
  const [credential, setCredential] = useState('')
  const [credentialDirty, setCredentialDirty] = useState(false)

  const configQuery = useQuery({
    queryKey: ['integrations', 'sop'],
    queryFn: async () => {
      const res = await api.get('/integrations/sop')
      return sopConfig.parse(res.data)
    },
  })

  // Sync the form state to the loaded config the first time it arrives
  // or whenever it's refetched.
  useEffect(() => {
    if (!configQuery.data) return
    setEndpointUrl(configQuery.data.endpointUrl)
    setAuthType(configQuery.data.authType)
    setCredential('')
    setCredentialDirty(false)
  }, [configQuery.data])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        endpointUrl: endpointUrl.trim(),
        authType,
      }
      // Only send a credential when the user has typed something.
      // Empty + dirty = explicit clear; empty + clean = leave as-is.
      if (credentialDirty) body.credential = credential
      await api.put('/integrations/sop', body)
    },
    onSuccess: () => {
      toast.success('SOP integration saved successfully.')
      void qc.invalidateQueries({ queryKey: ['integrations', 'sop'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      toast.error(fields?.endpointUrl ?? fields?.credential ?? message)
    },
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/integrations/sop/test')
      return sopTestResult.parse(res.data)
    },
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `${result.message} HTTP ${result.status}, ${result.latencyMs}ms.`,
        )
      } else {
        toast.error(`${result.message} (${result.latencyMs}ms)`)
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const hasStoredCred = Boolean(configQuery.data?.hasCredential)
  const credentialPlaceholder =
    authType === 'none'
      ? 'No credential needed.'
      : hasStoredCred && !credentialDirty
        ? '•••••••••••• already saved (leave blank to keep)'
        : authType === 'api_key'
          ? 'Paste the SOP API key'
          : 'Paste the bearer token'

  const isConnected = Boolean(configQuery.data?.endpointUrl)
  const testResult = testMutation.data
  const lastTestOk = testResult?.ok === true
  const lastTestFailed = testResult?.ok === false

  return (
    <div>
      <Breadcrumb current="SOP Integration" />

      {/* Header — same shape as every other Company Admin page (size-22
       *  brand-coloured icon, font-display h1, mt-1 muted subtitle). */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Plug
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            SOP Integration
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            Connect your SOP library to tasks.
          </p>
        </div>
        {/* Status pill — tells the admin at a glance whether the
         *  integration is currently wired up. Green = endpoint saved,
         *  grey = not configured yet. */}
        {!configQuery.isLoading ? (
          <span
            className={
              'shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ' +
              (isConnected
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10'
                : 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]')
            }
          >
            <span
              aria-hidden="true"
              className={
                'h-1.5 w-1.5 rounded-full ' +
                (isConnected ? 'bg-emerald-500' : 'bg-[color:var(--color-ink-muted)]')
              }
            />
            {isConnected ? 'Connected' : 'Not configured'}
          </span>
        ) : null}
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          Loading configuration…
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form card — two-thirds of the row on wide screens so the
           *  inputs read at a comfortable width without stretching to
           *  the full viewport. */}
          <section className="lg:col-span-2 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
            <h2 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-4">
              Endpoint
            </h2>
            <div className="space-y-4">
              <TextField
                name="endpointUrl"
                label="Endpoint URL"
                required
                placeholder="Enter the Endpoint URL"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                maxLength={500}
              />

              <div>
                <FieldLabel label="Authentication" htmlFor="sop-auth-type" />
                <select
                  id="sop-auth-type"
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
                >
                  {SOP_AUTH_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {AUTH_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>

              {authType !== 'none' ? (
                <div>
                  <FieldLabel label="Credential" htmlFor="sop-credential" />
                  <input
                    id="sop-credential"
                    type="password"
                    autoComplete="off"
                    placeholder={credentialPlaceholder}
                    value={credential}
                    onChange={(e) => {
                      setCredential(e.target.value)
                      setCredentialDirty(true)
                    }}
                    maxLength={2000}
                    className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                  />
                  <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                    Encrypted at rest with AES-256-GCM. Leave the field
                    blank to keep the saved credential; clear it
                    explicitly to remove.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mt-6 pt-4 border-t border-[color:var(--color-rule)] flex items-center gap-3">
              <Button
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!endpointUrl.trim()}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                onClick={() => testMutation.mutate()}
                loading={testMutation.isPending}
                disabled={!configQuery.data || !configQuery.data.endpointUrl}
                title={
                  !configQuery.data || !configQuery.data.endpointUrl
                    ? 'Save the endpoint URL first.'
                    : 'Send a GET against the configured endpoint.'
                }
              >
                Test Connection
              </Button>
              {configQuery.data?.updatedAt ? (
                <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                  Last updated {formatDateTime(configQuery.data.updatedAt)}
                </span>
              ) : null}
            </div>
          </section>

          {/* Side rail — last-test result + a "how this works" helper
           *  panel so first-time admins know what to point TaskMagix at. */}
          <aside className="space-y-4">
            <section
              className={
                'rounded-xl border p-4 ' +
                (lastTestOk
                  ? 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-500/25 dark:bg-emerald-500/[0.06]'
                  : lastTestFailed
                    ? 'border-[color:var(--color-priority-urgent)]/40 bg-[color:var(--color-priority-urgent)]/5'
                    : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)]')
              }
            >
              <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                Last Test
              </h3>
              {testResult ? (
                <>
                  <p
                    className={
                      'text-sm font-medium m-0 mb-2 ' +
                      (lastTestOk
                        ? 'text-emerald-700'
                        : 'text-[color:var(--color-priority-urgent)]')
                    }
                  >
                    {lastTestOk ? 'Success' : 'Failed'}
                  </p>
                  <dl className="text-xs text-[color:var(--color-ink-muted)] space-y-1">
                    <div className="flex gap-2">
                      <dt className="shrink-0 w-16">Status</dt>
                      <dd className="m-0 tabular-nums text-[color:var(--color-ink)]">
                        HTTP {testResult.status}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="shrink-0 w-16">Latency</dt>
                      <dd className="m-0 tabular-nums text-[color:var(--color-ink)]">
                        {testResult.latencyMs}ms
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="shrink-0 w-16">Message</dt>
                      <dd className="m-0 text-[color:var(--color-ink)] break-words">
                        {testResult.message}
                      </dd>
                    </div>
                  </dl>
                  {testResult.bodySnippet ? (
                    <pre className="mt-2 text-[11px] font-mono whitespace-pre-wrap break-words rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-2 max-h-48 overflow-auto m-0">
                      {testResult.bodySnippet}
                    </pre>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
                  No tests run yet. Save your endpoint, then click
                  &ldquo;Test Connection&rdquo; to verify TaskMagix can
                  reach it.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
              <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                How This Works
              </h3>
              <ol className="text-xs text-[color:var(--color-ink)] space-y-1.5 list-decimal pl-4 m-0">
                <li>
                  Point TaskMagix at your SOP library&apos;s REST endpoint.
                </li>
                <li>
                  Pick the auth mode your SOP service expects (API key
                  header, bearer token, or none).
                </li>
                <li>
                  Once connected, every published SOP appears in the
                  SOPs Library and can be attached to any task.
                </li>
              </ol>
            </section>
          </aside>
        </div>
      )}
    </div>
  )
}
